// SidePanel (HANDOFF §1/§4): every document opens here — voucher, manual
// entry, booking, appointment. 420 px at the end edge, 56 px head with close,
// body 16/20, footer slot for the ONE decisive button (52 px <PrimaryAction size="lg">).
// ≥1024: docked beside the content (the page stays usable).
// 768–1023: full-height overlay from the end edge with a scrim.
// <768: full screen.
// While open, the panel owns the screen's coral (see overlay-store).

import { useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { OverlayDepthContext, overlayStore, useOverlayDepthOf } from './overlay-store';

export interface SidePanelProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** The decisive action (+ its one-line hint). Rendered in the sticky footer. */
  footer?: ReactNode;
  /** DocHead row at the top of the body (document pill + sync pill + meta). */
  docHead?: ReactNode;
  /** Extra controls in the head, before the close button. */
  headActions?: ReactNode;
  'data-testid'?: string;
}

export const PANEL_DOCK_ID = 'e-panel-dock';

export function SidePanel(props: SidePanelProps) {
  if (!props.open) return null;
  return <OpenPanel {...props} />;
}

function OpenPanel({ title, onClose, children, footer, docHead, headActions, 'data-testid': testId }: SidePanelProps) {
  const id = useId();
  const titleId = `${id}-title`;
  // Live depth (follows the stack): a panel opened while another was still on
  // it turns coral once that one leaves.
  const depth = useOverlayDepthOf(id);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  useLayoutEffect(() => {
    overlayStore.push(id, 'panel');
    return () => overlayStore.remove(id);
  }, [id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && overlayStore.depthOf(id) === overlayStore.depth()) onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [id]);

  // The dock is rendered by the shell in the same commit as the page, so it is
  // looked up after mount. Without a dock (tests, standalone pages) the panel
  // floats as a fixed overlay at every width.
  const [host, setHost] = useState<{ el: HTMLElement; docked: boolean } | null>(null);
  useLayoutEffect(() => {
    const dock = document.getElementById(PANEL_DOCK_ID);
    setHost(dock ? { el: dock, docked: true } : { el: document.body, docked: false });
  }, []);
  useEffect(() => {
    if (host) closeRef.current?.focus();
  }, [host]);
  if (!host) return null;
  const node = (
    <OverlayDepthContext.Provider value={depth}>
      <div className="e-panel-scrim" onClick={onClose} aria-hidden="true" />
      <aside className={host.docked ? 'e-panel' : 'e-panel e-panel--floating'} role="dialog" aria-modal="false" aria-labelledby={titleId} data-testid={testId}>
        <header className="e-panel-head">
          <h2 id={titleId} className="e-panel-title">
            {title}
          </h2>
          {headActions}
          <button ref={closeRef} type="button" className="e-iconbtn e-panel-close" onClick={onClose} aria-label="إغلاق">
            <X aria-hidden="true" />
          </button>
        </header>
        <div className="e-panel-body">
          {docHead}
          {children}
        </div>
        {footer && <footer className="e-panel-footer">{footer}</footer>}
      </aside>
    </OverlayDepthContext.Provider>
  );
  return createPortal(node, host.el);
}

/** True while any side panel is open (the shell docks it ≥1024 px). */
export function usePanelOpen(): boolean {
  return useSyncExternalStore(overlayStore.subscribe, overlayStore.panelOpen, overlayStore.panelOpen);
}
